import cs from "classnames";
import React from "react";

import { scrollIntoView } from "src/common/utils/dom";

import "./Form.css";

const noop = (): void => {};

interface FormProps {
	className?: string;
	isSubmitted: boolean;
	onSubmit?(event: React.FormEvent): unknown;
}

export const Form: React.FunctionComponent<
	React.PropsWithChildren<FormProps>
> = ({ className, isSubmitted, onSubmit = noop, children }) => {
	const ref = React.useRef<HTMLFormElement>(null);

	const [key, setKey] = React.useState(0);
	React.useEffect(() => {
		if (isSubmitted) {
			return;
		}
		setKey(key => key + 1);
	}, [isSubmitted]);

	const scrollToError = React.useCallback(() => {
		const { current: form } = ref;
		if (form === null) {
			return;
		}
		const el = form.querySelector(
			".form__body:not(.form__body--hidden) .field--invalid"
		);
		if (el === null) {
			return;
		}
		scrollIntoView(el);
	}, []);

	const onSubmitInner = React.useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			scrollToError();
			onSubmit(event);
		},
		[scrollToError, onSubmit]
	);

	return (
		<form
			ref={ref}
			key={key}
			className={cs(
				"form",
				{ "form--submitted": isSubmitted },
				className
			)}
			onSubmit={onSubmitInner}
		>
			{children}
		</form>
	);
};

export const FormTitle: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <h1 className="form__title">{children}</h1>;

interface FormBodyProps {
	isHidden?: boolean;
}

export const FormBody: React.FunctionComponent<
	React.PropsWithChildren<FormBodyProps>
> = ({ isHidden = false, children }) => (
	<div className={cs("form__body", { "form__body--hidden": isHidden })}>
		{children}
	</div>
);

export const FormFooter: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <div className="form__footer">{children}</div>;

export const ValidationMessage: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => (
	<div className="form__validation-message">{children}</div>
);

export const FormCustomContainer: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => <div className="form__custom-container">{children}</div>;

export const FieldsetGrid: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <div className="fieldset-grid">{children}</div>;

export const Fieldset: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <fieldset className="fieldset">{children}</fieldset>;

export const Legend: React.FunctionComponent<React.PropsWithChildren> = ({
	children
}) => <h2 className="legend">{children}</h2>;
